import type { ReactNode } from 'react';
import { Card, CardAction, CardContent, CardHeader, CardTitle } from '@opengoal/ui';

export interface StepSectionProps {
  /** 1-based position, rendered as the circled ordinal. */
  step: number;
  title: string;
  /** Trailing controls on the header row. */
  extra?: ReactNode;
  children: ReactNode;
}

/**
 * One numbered step of the offboarding screen.
 *
 * Offboarding numbers its sections where onboarding deliberately does not. The
 * difference is real rather than an inconsistency: onboarding's steps are rows
 * in one list, where position is already visible, while offboarding's are four
 * separate forms stacked down a page, where nothing else conveys that they run
 * in a fixed order — and the order is exactly what the locking rule enforces.
 */
export function StepSection({ step, title, extra, children }: StepSectionProps) {
  return (
    <Card>
      <CardHeader className="items-center">
        <CardTitle className="flex items-center gap-2.5">
          <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
            {step}
          </span>
          {title}
        </CardTitle>
        {extra ? (
          <CardAction className="flex flex-wrap items-center gap-2">{extra}</CardAction>
        ) : null}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}
